import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  CircularProgress,
  Checkbox,
  Divider,
  Grid,
  FormControlLabel,
  FormGroup,
  TextField,
  Box,
  Tooltip,
  IconButton
} from "@material-ui/core";
import { Save as SaveIcon } from "@material-ui/icons";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useListSubFormState } from "src/apps/commons/form/hooks";
import { yupTrimToNull } from "src/utils/stringUtils";
import { ActiveStatus, SubHeader } from "src/apps/commons/dataDisplays";
import { ImageUpload } from "src/apps/commons/fileUploads";
import {
  IRemyndBrandContactSaveReq,
  IRemyndBrandContainer,
  IRemyndBrandSaveReq,
  IRemyndBrandSaveReqContainer
} from "../types";
import { paths } from "../utils";
import BrandContactModal from "./BrandContactModal";
import { SimpleSubTable } from "src/apps/commons/form";

const useStyles = makeStyles(theme => ({
  button: {
    paddingTop: "16px"
  },
  layoutButton: {
    flex: "1 1 auto"
  },
  skeleton: {
    display: "inline-flex",
    height: "72px",
    width: "100%"
  },

  subBlock: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2)
  },
  subHeader: {
    paddingBottom: "0px"
  },
  buttonPanel: {
    paddingTop: theme.spacing(2),
    "& > button": {
      marginRight: theme.spacing(2)
    }
  },
  contentLabel: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
    textAlign: "right"
  },
  contentDesc: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  }
}));

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------

interface Props {
  isLoading: boolean;
  isSaving: boolean;
  data?: IRemyndBrandContainer;
  onSubmit: (req: IRemyndBrandSaveReqContainer) => void;
}

type FormData = IRemyndBrandSaveReq & {
  contacts: IRemyndBrandContactSaveReq[];
};

type BrandContactModalState = {
  open: boolean;
  index?: number;
  data?: IRemyndBrandContactSaveReq;
};

const schema = yup.object().shape({
  nameTh: yup.string().transform(yupTrimToNull).required(),
  nameEn: yup.string().transform(yupTrimToNull).required(),
  synonymsTh: yup.string().transform(yupTrimToNull),
  synonymsEn: yup.string().transform(yupTrimToNull),
  homepageUrl: yup.string().transform(yupTrimToNull),
  supportUrl: yup.string().transform(yupTrimToNull),
  serviceUrl: yup.string().transform(yupTrimToNull),
  warrantyDetailUrl: yup.string().transform(yupTrimToNull),
  warrantyRegisterUrl: yup.string().transform(yupTrimToNull),
  imageId: yup.string().transform(yupTrimToNull),
  active: yup.boolean(),
  contacts: yup.array()
});

const Form: React.FC<Props> = props => {
  const { isLoading, isSaving, data, onSubmit } = props;
  const classes = useStyles();
  const router = useRouter();

  const defaultFormData = useMemo<Partial<FormData>>(
    () => ({
      nameTh: data?.brand?.nameTh ?? "",
      nameEn: data?.brand?.nameEn ?? "",
      synonymsTh: data?.brand?.synonymsTh ?? "",
      synonymsEn: data?.brand?.synonymsEn ?? "",
      homepageUrl: data?.brand?.homepageUrl ?? "",
      supportUrl: data?.brand?.supportUrl ?? "",
      serviceUrl: data?.brand?.serviceUrl ?? "",
      warrantyDetailUrl: data?.brand?.warrantyDetailUrl ?? "",
      warrantyRegisterUrl: data?.brand?.warrantyRegisterUrl ?? "",
      imageId: data?.brand?.imageId ?? "",
      active: data?.brand?.active ?? true,
      contacts:
        data?.contacts?.map(c => ({
          id: c.id,
          ord: c.ord,
          labelTh: c.labelTh,
          labelEn: c.labelEn,
          addressTh: c.addressTh,
          addressEn: c.addressEn,
          latitude: c.latitude,
          longitude: c.longitude,
          phoneNumber: c.phoneNumber,
          email: c.email,
          active: c.active
        })) ?? []
    }),
    [data]
  );
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<FormData>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  const {
    addItem: addContact,
    setItem: setContact,
    delItem: delContact,
    moveItem: moveContact,
    items: contacts
  } = useListSubFormState<IRemyndBrandContactSaveReq>(defaultFormData.contacts);
  const [contactModal, setContactModal] = useState<BrandContactModalState>({
    open: false
  });
  const handleMoveUp = (idx: number) =>
    idx > 0 ? moveContact(idx, idx - 1) : undefined;
  const handleMoveDown = (idx: number) =>
    idx < contacts.length - 1 ? moveContact(idx, idx + 1) : undefined;

  const handleCloseContactModal = () => {
    setContactModal({ open: false, index: undefined, data: undefined });
  };
  const handleSubmitContactModal = (req: IRemyndBrandContactSaveReq) => {
    if (contactModal.index !== undefined) {
      setContact(req, contactModal.index);
    } else {
      addContact(req);
    }
    handleCloseContactModal();
  };

  const ActiveCell = useCallback(
    (data: IRemyndBrandContactSaveReq) => <ActiveStatus value={data.active} />,
    []
  );

  const LinkCell = useCallback((data: IRemyndBrandContactSaveReq) => {
    const link = `https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}`;
    return (
      <>
        {data?.latitude && data?.longitude ? (
          <Tooltip title={link} placement="top">
            <IconButton
              aria-label="Link"
              href={link}
              target="_blank"
              rel="noreferrer"
            >
              <LocationOnIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton aria-label="Link" disabled>
            <LocationOnIcon />
          </IconButton>
        )}
      </>
    );
  }, []);

  const handleTransformAndSubmit = (form: FormData) => {
    onSubmit({
      brand: {
        nameTh: form.nameTh,
        nameEn: form.nameEn,
        synonymsTh: form.synonymsTh,
        synonymsEn: form.synonymsEn,
        homepageUrl: form.homepageUrl,
        supportUrl: form.supportUrl,
        serviceUrl: form.serviceUrl,
        warrantyDetailUrl: form.warrantyDetailUrl,
        warrantyRegisterUrl: form.warrantyRegisterUrl,
        imageId: form.imageId,
        active: form.active
      },
      contacts: contacts.map((it, idx) => ({
        ...it,
        ord: idx
      }))
    });
  };

  useEffect(() => {
    if (data) {
      reset(defaultFormData);
    }
  }, [reset, data, defaultFormData]);

  return (
    <>
      <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
        <Grid container alignItems="flex-start" spacing={2}>
          <Grid item xs={12} md={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="nameTh"
                control={control}
                defaultValue={defaultFormData.nameTh}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    error={!!errors.nameTh}
                    helperText={
                      errors.nameTh ? "กรุณาระบุ ชื่อ (TH)" : undefined
                    }
                    {...field}
                    label="ชื่อ (TH) *"
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="nameEn"
                control={control}
                defaultValue={defaultFormData.nameEn}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    error={!!errors.nameEn}
                    helperText={
                      errors.nameEn ? "กรุณาระบุ ชื่อ (EN)" : undefined
                    }
                    {...field}
                    label="ชื่อ (EN) *"
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="synonymsTh"
                control={control}
                defaultValue={defaultFormData.synonymsTh}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    {...field}
                    label="ชื่อเรียกอื่น ๆ (TH)"
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="synonymsEn"
                control={control}
                defaultValue={defaultFormData.synonymsEn}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    {...field}
                    label="ชื่อเรียกอื่น ๆ (EN)"
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="imageId"
              control={control}
              defaultValue={defaultFormData.imageId ?? ""}
              render={({ field: { value, onChange } }) => (
                <ImageUpload
                  fileId={value}
                  label={"รูปโลโก้แบรนด์"}
                  onChange={onChange}
                  disabled={isSaving}
                />
              )}
            />
          </Grid>

          <Grid item>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Controller
                      name="active"
                      control={control}
                      defaultValue={defaultFormData.active}
                      render={({ field: { value, onChange } }) => (
                        <Box ml={1}>
                          <Checkbox
                            color="primary"
                            checked={value}
                            onChange={onChange}
                            disabled={isSaving}
                          />
                        </Box>
                      )}
                    />
                  }
                  label="Active"
                />
              </FormGroup>
            )}
          </Grid>

          <Grid item xs={12}>
            <SubHeader title="ข้อมูลเพิ่มเติม" marginTop />
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="homepageUrl"
                control={control}
                defaultValue={defaultFormData.homepageUrl}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    {...field}
                    label="เว็บไซต์หลัก / Homepage"
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="supportUrl"
                control={control}
                defaultValue={defaultFormData.supportUrl}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    {...field}
                    label="URL ช่วยเหลือ / Support"
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="serviceUrl"
                control={control}
                defaultValue={defaultFormData.serviceUrl}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    {...field}
                    label="URL ศูนย์บริการ / Services"
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="warrantyDetailUrl"
                control={control}
                defaultValue={defaultFormData.warrantyDetailUrl}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    {...field}
                    label="URL รายละเอียดการรับประกัน"
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="warrantyRegisterUrl"
                control={control}
                defaultValue={defaultFormData.warrantyRegisterUrl}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    {...field}
                    label="URL ลงทะเบียนรับประกัน"
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <SubHeader
              title={`ข้อมูลการติดต่อ/ศูนย์บริการ (${contacts.length})`}
              marginTop
            />
          </Grid>

          <Grid item xs={12}>
            <SimpleSubTable<IRemyndBrandContactSaveReq>
              data={contacts}
              onClickAdd={() => setContactModal({ open: true })}
              onClickEdit={(req, idx) =>
                setContactModal({
                  open: true,
                  index: idx,
                  data: req
                })
              }
              onClickDelete={(_, idx) => delContact(idx)}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              showIndex
              columns={[
                {
                  label: "Label",
                  minWidth: 140,
                  renderCell: req => `${req.labelTh} (${req.labelEn})`
                },
                {
                  label: "Address (TH)",
                  minWidth: 240,
                  renderCell: req => req.addressTh
                },
                {
                  label: "Address (EN)",
                  minWidth: 240,
                  renderCell: req => req.addressEn
                },
                {
                  label: "Phone Number",
                  minWidth: 160,
                  renderCell: req => req.phoneNumber
                },
                {
                  label: "Email",
                  width: 180,
                  renderCell: req => req.email
                },
                {
                  label: "Location",
                  width: 80,
                  align: "center",
                  renderCell: LinkCell
                },
                {
                  label: "Active",
                  width: 80,
                  align: "center",
                  renderCell: ActiveCell
                }
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <Box mt={2} mb={1}>
              <Divider light />
            </Box>
          </Grid>

          <Grid item className={classes.layoutButton} />

          <Grid item className={classes.button}>
            <Button
              variant="contained"
              onClick={() => router.push(paths.index)}
              disabled={isSaving || isLoading}
            >
              Cancel
            </Button>
          </Grid>

          <Grid item className={classes.button}>
            <Button
              variant="contained"
              color="primary"
              disabled={isSaving || isLoading}
              type="submit"
              startIcon={
                isSaving ? (
                  <CircularProgress color="secondary" size={20} />
                ) : (
                  <SaveIcon />
                )
              }
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </form>

      {contactModal.open && (
        <BrandContactModal
          label="ข้อมูลการติดต่อ/ศูนย์บริการ"
          open={contactModal.open}
          defaultValue={contactModal.data}
          handleCloseModal={handleCloseContactModal}
          handleSubmitModal={handleSubmitContactModal}
        />
      )}
    </>
  );
};

export default Form;
